import * as React from "react";
import "./ConversationViewer.scss";
import { Conversation, ConversationParticipant, Message, MessageSegmentTypes } from "../types";
import moment from "moment";
import TextareaAutosize from "react-autosize-textarea";
import ReactTextFormat from "react-text-format";
import { useState, useEffect, useRef } from "react";
import { get, patch, post } from "../../../utils/fetch";
import { Page, RootState } from "../../types";
import { useSelector, useDispatch } from "react-redux";
import { User } from "../../session/types";
import MomentRelativeDisplay from "../../../components/MomentRelativeDisplay";
import { fetchParticipantUser } from "../../session/actions";
import { subscribeChannels, unsubscribeChannel } from "../../pubnub/actions";
import { getUnreadCount } from "../../SiteHeader/actions";
import useVisibility from "../../../components/effects/useVisibility";
import { PubNubMsgState } from "../../pubnub/messages/reducer";
import Tooltip from "../../../components/Tooltip";
import { AttachmentViewer } from "./AttachmentViewer";
import flash from "../../flash/actions";
import UploadAttachment from "../../../components/UploadAttachment";

type Props = {
  conversationId: string;
  setLoading?: (value: boolean) => void;
  isPatient?: boolean;
};

const customLinkDecorator = (decoratedHref: string, decoratedText: string): React.ReactNode => {
  return (
    <a href={decoratedHref} target="_blank" rel="noopener noreferrer" className="message-link">
      {decoratedText}
    </a>
  );
};

const ConversationViewer: React.FunctionComponent<Props> = ({
  conversationId,
  setLoading,
  isPatient
}: Props) => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [newMessage, setNewMessage] = useState<string>("");
  const currentUser: User | null = useSelector(
    (state: RootState) => state.session.data.currentUser
  );
  const newPubNubObject: PubNubMsgState = useSelector((state: RootState) => state.pubnub.message);
  const [currentUserParticipant, setCurrentUserParticipant] = useState<ConversationParticipant>();
  const [otherParticipants, setOtherParticipants] = useState<ConversationParticipant[]>([]);
  const [showPatientMessageDisclaimer, setShowPatientMessageDisclaimer] = useState<boolean>(false);
  const hidePatient = conversation?.participants.some(
    x => x.systemUserId === conversation.patient.id
  );
  const pageSize = 25;
  const scrollBottomRef = useRef<HTMLDivElement>(null);
  const timeStampInterval = 120000;
  const dispatch = useDispatch();
  const isVisible = useVisibility();
  const [scrollPosition, setSrollPosition] = useState(0);
  const scrollLimitApiConst = 20;
  const [nextUrlMutex, setNextUrlMutex] = useState<boolean>(false);
  const [conversationNextUrl, setConversationNextUrl] = useState<string>();

  const handleScroll = () => {
    if (scrollBottomRef?.current) {
      //Grabbing the percent of the user scrolling. 100 = scrolled to the top
      const scrollTopPercent = Math.round(
        (scrollBottomRef.current.scrollTop /
          (scrollBottomRef.current.scrollHeight - scrollBottomRef.current.clientHeight)) *
          100
      );
      setSrollPosition(scrollTopPercent);
    }
  };

  useEffect(() => {
    if (scrollPosition <= scrollLimitApiConst && conversationNextUrl && !nextUrlMutex) {
      setNextUrlMutex(true);
      getOlderMessages(conversationNextUrl);
    }
  }, [scrollPosition]);

  useEffect(() => {
    if (isVisible) {
      markConversationRead();
    }
  }, [isVisible]);

  useEffect(() => {
    if (scrollBottomRef.current) {
      scrollBottomRef.current.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      if (scrollBottomRef.current) {
        scrollBottomRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [scrollBottomRef.current]);

  const getConversation = async () => {
    const url = `/api/conversations/${conversationId}/?message_page_size=${pageSize}&message_page=1`;
    setLocalLoading(true);
    const resp = await get<Conversation>(url);
    setConversationNextUrl("");
    if (resp.data) {
      setConversation({
        ...resp.data,
        messages: resp.data.messages.sort(
          (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
        )
      });

      const currentParticipant = resp.data.participants.find(
        x => x.systemUserId === currentUser?.id
      );

      if (currentParticipant) {
        setCurrentUserParticipant(currentParticipant);
        setOtherParticipants(resp.data.participants.filter(x => x.id != currentParticipant?.id));
      }

      if (resp.data.messageCount > pageSize) {
        //Figure out if a nextUrl is needed
        const newNextUrl = `/api/conversations/${conversationId}/messages/?page_size=${pageSize}&page=2&message_page=2`;
        setNextUrlMutex(false);
        setConversationNextUrl(newNextUrl);
      }
    }
    scrollToBottomOfMessages();
    markConversationRead();
    setLocalLoading(false);
  };

  const getOlderMessages = async (nextUrl = "") => {
    if (!nextUrl) {
      return;
    }
    const resp = await get<Page<Message>>(nextUrl);
    setConversationNextUrl("");
    if (resp.data) {
      if (nextUrl && conversation && resp.data.results) {
        if (resp.data.next) {
          setConversationNextUrl(resp.data.next);
        }
        setNextUrlMutex(false);
        setConversation({
          ...conversation,
          messages: conversation.messages
            .concat(resp.data.results)
            .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime())
        });
        const resultsLength = resp.data.results.length - 1;
        // Scroll to the message that was last before this api call
        const lastMessage = document.getElementById(`message-${resultsLength}`);
        if (lastMessage) {
          window.setTimeout(() => {
            lastMessage.scrollIntoView();
          }, 0);
        }
      }
    }
  };

  const getMessage = async () => {
    if (!currentUserParticipant) return;
    const newMessageObject = newPubNubObject.byId[currentUserParticipant.id];
    if (!newMessageObject.newestMessageId) return;

    //Check to see if the new message id is already in our messages array
    if (conversation?.messages.find(m => m.id === newMessageObject.newestMessageId)) {
      return;
    }
    const url = `/api/conversations/${conversationId}/messages/${newMessageObject.newestMessageId}/`;
    const resp = await get<Message>(url);

    if (resp.data && conversation) {
      setConversation({
        ...conversation,
        messages: conversation.messages.concat(resp.data)
      });

      scrollToBottomOfMessages();
      markConversationRead();
    }

    setLocalLoading(false);
  };

  const updateReadReciepts = () => {
    if (!currentUserParticipant || !conversation) return;
    const readAt: Date = newPubNubObject.byId[currentUserParticipant.id].readAt;
    //This is the best way to copy conversation over without mutating it. As it is heavily nested
    const messageCopy = JSON.parse(JSON.stringify(conversation.messages));
    messageCopy.filter(
      m =>
        new Date(m.created) < readAt &&
        m.participantActivity.forEach(a => {
          if (!a.read) {
            a.read = readAt.toUTCString();
          }
        })
    );
    setConversation({
      ...conversation,
      messages: messageCopy
    });
  };

  const endConversationUI = () => {
    if (!conversation) {
      return;
    }
    setConversation({
      ...conversation,
      readOnly: true
    });
  };

  const markConversationRead = async () => {
    if (isVisible) {
      const url = `/api/conversations/${conversationId}/read/`;
      const resp = await post<Message>(url, { readAt: new Date().toISOString() });
      dispatch(getUnreadCount());
    }
  };

  useEffect(() => {
    if (!currentUser) {
      (async function getUser() {
        await dispatch(fetchParticipantUser());
      })();
    }
  }, []);

  useEffect(() => {
    if (!conversationId || !currentUser) return;
    getConversation();
  }, [conversationId, currentUser]);

  // For new messages and read reciepts from the pubnub event
  useEffect(() => {
    if (
      !newPubNubObject ||
      !currentUserParticipant ||
      (currentUserParticipant && !newPubNubObject.byId[currentUserParticipant.id])
    )
      return;
    if (newPubNubObject.byId[currentUserParticipant.id].newMessage) {
      getMessage();
    }
    if (
      newPubNubObject.byId[currentUserParticipant.id].readAt &&
      !newPubNubObject.byId[currentUserParticipant.id].newMessage
    ) {
      updateReadReciepts();
    }
    if (newPubNubObject.byId[currentUserParticipant.id].conversationEnd) {
      endConversationUI();
    }
  }, [newPubNubObject]);

  useEffect(() => {
    setShowPatientMessageDisclaimer(false);
    scrollToBottomOfMessages();
  }, [conversation]);

  useEffect(() => {
    if (currentUserParticipant) {
      dispatch(subscribeChannels([currentUserParticipant.id]));
    }
    const prevUserParticipant = currentUserParticipant;
    return () => {
      if (prevUserParticipant) {
        dispatch(unsubscribeChannel(prevUserParticipant.id));
      }
    };
  }, [currentUserParticipant]);

  const scrollToBottomOfMessages = () => {
    if (scrollBottomRef && scrollBottomRef.current)
      scrollBottomRef.current.scrollTop = scrollBottomRef.current.scrollHeight;
  };

  const sendMessage = async () => {
    if (!newMessage) return;

    setLocalLoading(true);
    const url = `/api/conversations/${conversationId}/messages/`;
    const resp = await post<Message>(url, { body: newMessage });

    if (resp.data && conversation) {
      setNewMessage("");
      setConversation({ ...conversation, messages: conversation.messages.concat(resp.data) });

      if (isPatient) {
        setTimeout(() => {
          setShowPatientMessageDisclaimer(true);
          scrollToBottomOfMessages();
          setTimeout(() => {
            setShowPatientMessageDisclaimer(false);
          }, 30000);
        }, 1000);
      }
    }
    setLocalLoading(false);
  };

  const endConversation = async () => {
    setLocalLoading(true);

    const url = `/api/conversations/${conversationId}/end/`;
    const resp = await patch<Conversation>(url, null);

    if (resp.data) {
      setConversation({
        ...resp.data,
        messages: resp.data.messages.sort(
          (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
        )
      });
    }

    setLocalLoading(false);
  };

  const showTimeStamp = (currentMessage: Message, idx: number) => {
    if (idx > 1 && conversation) {
      const previousMessageCreatedTime = conversation.messages[idx - 1].created;
      if (
        new Date(currentMessage.created).getTime() -
          new Date(previousMessageCreatedTime).getTime() >
        timeStampInterval
      ) {
        return true;
      }
    }
    return false;
  };

  const appendAttachmentToConversation = async (attachmentId: string) => {
    const url = `/api/conversations/${conversationId}/messages/`;
    const resp = await post<Message>(url, { attachments: [attachmentId] });

    if (resp.data && conversation) {
      setConversation({
        ...conversation,
        messages: conversation.messages.concat(resp.data)
      });
      scrollToBottomOfMessages();
    }
    setLoading ? setLoading(false) : setLocalLoading(false);
  };

  return (
    <div className="conversation-container">
      {localLoading && <div className="loading"></div>}
      {conversation && (
        <>
          <div className="participant-header ellipsis">
            {isPatient
              ? conversation.organization.name
              : otherParticipants.map(n => n.displayName).join(", ")}
          </div>

          {!hidePatient && (
            <div className="ellipsis patient-header">
              Patient: {conversation.patient.givenName} {conversation.patient.familyName}
            </div>
          )}

          {conversation.readOnly && <div className="ended">This conversation has ended.</div>}

          {isPatient && (
            <div className="patient-participants ellipsis">
              {otherParticipants.map(n => n.displayName).join(", ")}
            </div>
          )}

          <div className="message-list" ref={scrollBottomRef}>
            <div className="timestamp flex-justify-center pb-10">
              Conversation started {moment(conversation.messages[0].created).format("LLLL")}
            </div>

            {conversation.messages.map((message, idx) => (
              <div
                key={idx}
                className={
                  `mb-10 ` +
                  (message.senderParticipantId === currentUserParticipant?.id ? "user" : "other")
                }
              >
                {otherParticipants.length > 1 && (
                  <div className={`name`}>{message.senderDisplayName}</div>
                )}

                {showTimeStamp(message, idx) && (
                  <div className="timestamp">
                    <MomentRelativeDisplay datetime_string={message.created} />
                  </div>
                )}

                <div className={`message`} id={`message-${idx}`}>
                  <div className={`body`}>
                    {message.segments.map((segment, segmentIndex) => (
                      <div key={`segmentIndex-${segmentIndex}`}>
                        {segment.type === MessageSegmentTypes.Text && (
                          <span>
                            <ReactTextFormat linkDecorator={customLinkDecorator}>
                              {segment.text}
                            </ReactTextFormat>
                          </span>
                        )}
                        {segment.type === MessageSegmentTypes.Attachment && (
                          <AttachmentViewer url={segment.attachment.url} />
                        )}
                      </div>
                    ))}
                    {!isPatient &&
                      message.participantActivity
                        .filter(
                          x =>
                            x.participantId != currentUserParticipant?.id &&
                            message.senderParticipantId === currentUserParticipant?.id
                        )
                        .map(participant => (
                          <div
                            key={participant.id}
                            className={"read-receipt " + (!participant.read ? "bold" : "")}
                          >
                            {participant.read ? (
                              <span>
                                Read -{" "}
                                <MomentRelativeDisplay
                                  datetime_string={participant.read.toString()}
                                />
                              </span>
                            ) : (
                              "Unread"
                            )}
                          </div>
                        ))}
                  </div>
                </div>
              </div>
            ))}

            {showPatientMessageDisclaimer && (
              <div className="sent-disclaimer">
                {conversation.organization.secureConversationActiveFooter}
              </div>
            )}
          </div>
          {!conversation.readOnly && (
            <div className="m-15">
              {!isPatient && (
                <div className="mt-10 mb-10 flex-display-justify-center">
                  <button className="light-border" id="end-conversation" onClick={endConversation}>
                    <i className="fas fa-comment-slash mr-5"></i> I want to end this conversation.
                  </button>
                </div>
              )}

              <div className="flex">
                <UploadAttachment
                  setLoading={setLoading}
                  addAttachment={appendAttachmentToConversation}
                />

                <TextareaAutosize
                  id="secure-message-textarea"
                  className="form-control text-area"
                  value={newMessage}
                  onChange={e => setNewMessage(e.currentTarget.value)}
                  placeholder="Text Message"
                />
                <button
                  className="ml-10"
                  id="send-button"
                  disabled={!newMessage}
                  onClick={sendMessage}
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {conversation.readOnly && isPatient && (
            <div className="message-footer">{conversation?.organization.secureMessageFooter}</div>
          )}
        </>
      )}
    </div>
  );
};
export default ConversationViewer;
